import { EEventAnalyticsKpiSection, IEventAnalyticsKpiCardConfig } from './admin-event-analytics.interface';

/** Every KPI card the KPIs tab renders (grouped by `section`); the Overview tab shows the `showInOverview` subset. */
export const EVENT_ANALYTICS_KPI_CARDS: IEventAnalyticsKpiCardConfig[] = [
  // Completion & adoption
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.campaignCompletionRate,
    key: 'campaignCompletionRate',
    section: EEventAnalyticsKpiSection.completionAdoption,
    showInOverview: true,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => safeNumber(kpis.reportAdaptionConversion),
    key: 'reportAdoptionConversion',
    section: EEventAnalyticsKpiSection.completionAdoption,
    showInOverview: true,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => safeNumber(kpis.briefingAdaptionConversion),
    key: 'briefingAdoptionConversion',
    section: EEventAnalyticsKpiSection.completionAdoption,
    showInOverview: true,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => safeRatio(kpis.campaignsWithReports, kpis.campaignCreationsFinished),
    key: 'campaignsWithReports',
    section: EEventAnalyticsKpiSection.completionAdoption,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => safeRatio(kpis.campaignsWithBriefings, kpis.campaignCreationsFinished),
    key: 'campaignsWithBriefings',
    section: EEventAnalyticsKpiSection.completionAdoption,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.reportOpenRate,
    key: 'reportOpenRate',
    section: EEventAnalyticsKpiSection.completionAdoption,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.briefingOpenRate,
    key: 'briefingOpenRate',
    section: EEventAnalyticsKpiSection.completionAdoption,
    valueType: 'percentage',
  },

  // Usage
  {
    getTotal: (kpis) => kpis.totalUserGroups,
    getValue: (kpis) => kpis.activeUserGroups,
    key: 'activeUserGroups',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getTotal: (kpis) => kpis.totalUsers,
    getValue: (kpis) => kpis.activeUsers,
    key: 'activeUsers',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getTotal: (kpis) => kpis.totalUsers,
    getValue: (kpis) => kpis.activeUsersFrontend,
    key: 'activeUsersFrontend',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getTotal: (kpis) => kpis.totalUsers,
    getValue: (kpis) => kpis.activeUsersMcp,
    key: 'activeUsersMcp',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getTotal: (kpis) => kpis.totalUsers,
    getValue: (kpis) => kpis.activeUsersAvgDaily,
    hideInKpisTab: true,
    key: 'activeUsersAvgDaily',
    section: EEventAnalyticsKpiSection.usage,
    showInOverview: true,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.totalUsers,
    hideInKpisTab: true,
    key: 'totalUsers',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.totalUserGroups,
    hideInKpisTab: true,
    key: 'totalUserGroups',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.activeCampaigns,
    key: 'activeCampaigns',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.campaignCreationsStarted,
    key: 'campaignCreationsStarted',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.campaignCreationsFinished,
    hideInKpisTab: true,
    key: 'campaignCreationsFinished',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.reportsCreated,
    key: 'reportsCreated',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.avgReportsPerCampaign,
    key: 'avgReportsPerCampaign',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'float',
  },
  {
    getValue: (kpis) => kpis.briefingsCreated,
    key: 'briefingsCreated',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.avgBriefingsPerCampaign,
    key: 'avgBriefingsPerCampaign',
    section: EEventAnalyticsKpiSection.usage,
    valueType: 'float',
  },

  // Engagement
  {
    getValue: (kpis) => kpis.avgTimeToCampaignCreationFinished,
    key: 'avgTimeToCampaignCreationFinished',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.avgTimeInAnalyticsPopupOverview,
    key: 'avgTimeInAnalyticsPopupOverview',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.avgTimeInAnalyticsPopupDeliverableView,
    key: 'avgTimeInAnalyticsPopupDeliverableView',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.avgViewsPerReport,
    key: 'avgViewsPerReport',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'float',
  },
  {
    getValue: (kpis) => kpis.avgViewsPerBriefing,
    key: 'avgViewsPerBriefing',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'float',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.campaignsWithAnalyticsViewed,
    key: 'campaignsWithAnalyticsViewed',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.campaignsWithAnalyticsViewedMcp,
    key: 'campaignsWithAnalyticsViewedMcp',
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'percentage',
  },
  {
    getValue: (kpis) => kpis.timeToFirstCampaignCreationFinished,
    key: 'timeToFirstCampaignCreationFinished',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.timeToFirstAnalyticsPopupOpened,
    key: 'timeToFirstAnalyticsPopupOpened',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.timeToFirstBriefingCreated,
    key: 'timeToFirstBriefingCreated',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.timeToFirstReportPublished,
    key: 'timeToFirstReportPublished',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },
  {
    getValue: (kpis) => kpis.timeToFirstMcpToolCall,
    key: 'timeToFirstMcpToolCall',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.engagement,
    valueType: 'duration',
  },

  // Costs
  {
    getValue: (kpis) => kpis.globalCosts,
    key: 'globalCosts',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.costs,
    valueType: 'currency',
  },
  {
    getValue: (kpis) => kpis.avgDailyCosts,
    key: 'avgDailyCosts',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.costs,
    valueType: 'currency',
  },
  {
    getValue: (kpis) => kpis.avgCampaignCosts,
    key: 'avgCampaignCosts',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.costs,
    valueType: 'currency',
  },
  {
    getValue: (kpis) => kpis.highestCampaignCosts,
    key: 'highestCampaignCosts',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.costs,
    valueType: 'currency',
  },

  // MCP connector
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.mcpAdoptionRate,
    key: 'mcpAdoptionRate',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'percentage',
  },
  {
    getValue: (kpis) => kpis.mcpConnectedUsers,
    key: 'mcpConnectedUsers',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.mcpSessions,
    key: 'mcpSessions',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.mcpToolCalls,
    key: 'mcpToolCalls',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'integer',
  },
  {
    getValue: (kpis) => kpis.avgMcpToolCallsPerSession,
    key: 'avgMcpToolCallsPerSession',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'float',
  },
  {
    getValue: (kpis) => kpis.avgMcpActiveDaysPerUser,
    key: 'avgMcpActiveDaysPerUser',
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'float',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.mcpToolErrorRate,
    key: 'mcpToolErrorRate',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'percentage',
  },
  {
    enableProgressBar: true,
    getValue: (kpis) => kpis.mcpToolRejectedRate,
    key: 'mcpToolRejectedRate',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'percentage',
  },
  {
    getValue: (kpis) => kpis.avgMcpCallDuration,
    key: 'avgMcpCallDuration',
    lowerIsBetter: true,
    section: EEventAnalyticsKpiSection.mcp,
    valueType: 'milliseconds',
  },
];

export const EVENT_ANALYTICS_OVERVIEW_KPI_CARDS: IEventAnalyticsKpiCardConfig[] = EVENT_ANALYTICS_KPI_CARDS.filter(
  (card) => card.showInOverview,
);

/** The subset of `EVENT_ANALYTICS_KPI_CARDS` the KPIs tab's full breakdown actually renders. */
export const EVENT_ANALYTICS_KPI_TAB_CARDS: IEventAnalyticsKpiCardConfig[] = EVENT_ANALYTICS_KPI_CARDS.filter(
  (card) => !card.hideInKpisTab,
);

export const EVENT_ANALYTICS_KPI_SECTION_ORDER: EEventAnalyticsKpiSection[] = [
  EEventAnalyticsKpiSection.completionAdoption,
  EEventAnalyticsKpiSection.usage,
  EEventAnalyticsKpiSection.engagement,
  EEventAnalyticsKpiSection.costs,
  EEventAnalyticsKpiSection.mcp,
];

function safeRatio(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

/** Coerces a non-finite backend value (e.g. `NaN` from a 0/0 division, or a missing field) to 0, so the KPI card shows "0.0%" instead of a dash or "NaN%". */
function safeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
